import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Building2, ChevronRight, Home, Menu, Search, SearchX, X } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Dialog, DialogClose, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { EnvironmentBadge } from '@/components/layout/EnvironmentBadge'
import { useTabAccess } from '@/hooks/useTabAccess'
import { flattenDashboardNavCards } from '@/lib/dashboardNav'
import { iconForDashboardNav } from '@/lib/dashboardNavIcons'
import { navFeatureFor } from '@/lib/featureFlags'
import { homeTile, isTileAllowed } from '@/lib/homeLayouts'
import {
  buildMainMenu,
  filterMainMenu,
  splitHref,
  visibleMenuRows,
  type MainMenuEntry,
  type MainMenuRow,
  withMenuShortcuts,
} from '@/lib/mainMenu'
import { allowedTabsFor } from '@/lib/pageTabs'
import { PERMISSIONS, navPermissionsFor, usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { useTenant } from '@/providers/TenantProvider'

const TREE_ID = 'main-menu-tree'
const rowDomId = (id: string) => `main-menu-row-${id}`

const HOME_HREF = '/dashboard'
/** Pinned above the A–Z list: Home has no tile of its own. */
const HOME_ROW: MainMenuRow = {
  kind: 'page',
  id: 'home',
  label: 'Home',
  href: HOME_HREF,
  hasTabs: false,
  expanded: false,
  forced: false,
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
const SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl K'

/**
 * The ☰ main menu (z8nrz7f7g1, z8nrz7f7g3): a drawer from the left listing
 * every page the user may open A–Z, each page's tabs beneath it, with Home
 * pinned on top. ⌘K / Ctrl+K opens and closes it from anywhere.
 *
 * Built on the Radix dialog primitives for the focus trap, Escape and focus
 * return, with `DialogPrimitive.Content` because the shared `DialogContent`
 * hard-codes a centred modal (same reason as MobileTenantSheet).
 */
export function MainMenu() {
  const [open, setOpen] = useState(false)
  const openRef = useRef(open)
  openRef.current = open
  // Where focus was when the shortcut opened the menu, to go back there on
  // close rather than to the ☰ button.
  const returnFocusTo = useRef<HTMLElement | null>(null)

  // Only one MainMenu is ever mounted: the TopBar's, or Home's (Layout hides
  // the TopBar on /dashboard), so the shortcut has one listener.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      // ⌘K on a Mac, where Ctrl+K deletes to the end of the line in a field.
      const modifier = IS_MAC ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey
      if (!modifier || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'k') return
      if (e.repeat || e.isComposing) return
      if (!openRef.current) {
        // Never over another dialog: leaving from here would drop what is in it.
        if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return
        returnFocusTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      }
      e.preventDefault()
      setOpen(!openRef.current)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild onClick={() => (returnFocusTo.current = null)}>
        <button
          type='button'
          title={`Main menu (${SHORTCUT_LABEL})`}
          aria-label='Main menu'
          aria-keyshortcuts={IS_MAC ? 'Meta+K' : 'Control+K'}
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <Menu className='h-[18px] w-[18px]' />
        </button>
      </DialogTrigger>
      <DialogPortal>
        <DialogOverlay className='bg-slate-900/25 backdrop-blur-[2px]' />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onCloseAutoFocus={(e) => {
            const target = returnFocusTo.current
            returnFocusTo.current = null
            if (target?.isConnected) {
              e.preventDefault()
              target.focus()
            }
          }}
          className='fixed inset-y-0 left-0 z-50 flex w-60 max-w-[85vw] flex-col overflow-hidden rounded-r-xl bg-white shadow-2xl ring-1 ring-black/5 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left'
        >
          {/* Content unmounts on close, so every open starts with an empty filter. */}
          <MainMenuPanel onNavigate={() => setOpen(false)} />
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

function MainMenuPanel({ onNavigate }: { onNavigate: () => void }) {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { can, canAny, isSuperAdmin } = usePermission()
  const tabAccess = useTabAccess()
  const { tenants, selectedTenantId } = useTenant()
  const tenantName = tenants.find((t) => t.id === selectedTenantId)?.name

  // The Home tile gate, so the menu and Home cannot disagree on a page. Unlike
  // Home, a page whose feature is off is hidden rather than greyed: a menu
  // entry that cannot be opened is noise.
  const tileAccess = { isSuperAdmin, canAnyRoute: (to: string) => canAny(navPermissionsFor(to)) }
  const built = buildMainMenu(flattenDashboardNavCards(), {
    isAllowed: (card) =>
      isTileAllowed({ to: card.href, superAdminOnly: card.superAdminOnly }, tileAccess) &&
      navFeatureFor(card.href).every(tabAccess.featureOn),
    tabsFor: (path) => allowedTabsFor(path, tabAccess),
  })
  // Purchase Orders, listed by its own name as well as under Configure.
  const entries = withMenuShortcuts(
    built,
    can(PERMISSIONS.purchaseOrdersManage)
      ? [{ id: 'po-config', label: 'Purchase Orders', href: '/configure?tab=purchase-orders', tabs: [] }]
      : [],
  )

  // One row is the current one: the tab when the URL names one, else the page.
  const currentTab = new URLSearchParams(search).get('tab')
  const onPath = (row: MainMenuRow) => splitHref(row.href).path === pathname
  const isCurrent = (row: MainMenuRow) => {
    if (!onPath(row)) return false
    const { tab } = splitHref(row.href)
    if (row.kind === 'tab' || tab !== null) return tab === currentTab
    return !(row.hasTabs && currentTab)
  }

  const [query, setQuery] = useState('')
  // Opens on the page the user is on, with its tabs showing.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const page = entries.find((e) => splitHref(e.href).path === pathname && e.tabs.length > 0)
    return new Set(page ? [page.id] : [])
  })
  const [activeId, setActiveId] = useState<string | null>(null)

  const trimmed = query.trim().toLowerCase()
  const showHome = !trimmed || HOME_ROW.label.toLowerCase().includes(trimmed)
  const pageRows = visibleMenuRows(filterMainMenu(entries, query), expandedIds)
  const rows = showHome ? [HOME_ROW, ...pageRows] : pageRows
  // A row that a new filter hid falls back to the first one.
  const activeIndex = Math.max(
    0,
    rows.findIndex((r) => r.id === activeId),
  )
  const active = rows[activeIndex] as MainMenuRow | undefined

  const activeRowId = active?.id
  useEffect(() => {
    if (activeRowId) document.getElementById(rowDomId(activeRowId))?.scrollIntoView({ block: 'nearest' })
  }, [activeRowId])

  const setExpanded = (id: string, expanded: boolean) =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (expanded) next.add(id)
      else next.delete(id)
      return next
    })

  const go = (href: string) => {
    navigate(href)
    onNavigate()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!active) return
    const move = (index: number) => setActiveId(rows[Math.min(Math.max(index, 0), rows.length - 1)].id)
    switch (e.key) {
      case 'ArrowDown':
        move(activeIndex + 1)
        break
      case 'ArrowUp':
        move(activeIndex - 1)
        break
      case 'Enter':
        go(active.href)
        break
      // Left and Right open and close pages only while the filter is empty, so
      // they still move the caret through typed text.
      case 'ArrowRight':
        if (query || active.kind !== 'page' || !active.hasTabs) return
        if (active.expanded) move(activeIndex + 1)
        else setExpanded(active.id, true)
        break
      case 'ArrowLeft':
        if (query) return
        if (active.kind === 'tab') setActiveId(active.parentId)
        else if (active.expanded) setExpanded(active.id, false)
        else return
        break
      default:
        return
    }
    e.preventDefault()
  }

  const rowProps = (row: MainMenuRow) => ({
    row,
    query: trimmed,
    active: row.id === active?.id,
    current: row.id === HOME_ROW.id ? pathname === HOME_HREF : isCurrent(row),
    onHover: () => setActiveId(row.id),
    onNavigate,
  })

  return (
    <>
      {/* Brand header, the height of the top bar it opens over. */}
      <div className='flex h-10 shrink-0 items-center gap-1.5 pl-3 pr-1.5'>
        <DialogTitle className='sr-only'>Main menu</DialogTitle>
        <img src='/images/title21-lockup.png' alt='Title21' width={1322} height={336} className='h-auto w-[4.5rem]' />
        <EnvironmentBadge />
        <DialogClose
          className='ml-auto flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700'
          aria-label='Close'
        >
          <X className='h-3.5 w-3.5' />
        </DialogClose>
      </div>

      <div className='shrink-0 px-2 pb-2'>
        <div className='group relative'>
          <Search className='pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600' />
          <input
            autoFocus
            type='text'
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveId(null)
            }}
            onKeyDown={onKeyDown}
            placeholder='Find a page or tab'
            aria-label='Find a page or tab'
            role='combobox'
            aria-expanded={rows.length > 0}
            aria-haspopup='tree'
            aria-controls={TREE_ID}
            aria-activedescendant={active ? rowDomId(active.id) : undefined}
            aria-autocomplete='list'
            className='h-7 w-full rounded-md border border-transparent bg-gray-100 pl-7 pr-11 text-xs text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100'
          />
          <kbd className='pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 rounded border border-gray-200 bg-white px-1 font-sans text-[9px] font-medium text-gray-400'>
            {SHORTCUT_LABEL}
          </kbd>
        </div>
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto border-t border-gray-100 px-1.5 py-1'>
        {/* Always rendered: the filter box's aria-controls points at it. */}
        <div id={TREE_ID} role='tree' aria-label='Pages'>
          {showHome && (
            <>
              <HomeRow {...rowProps(HOME_ROW)} />
              {pageRows.length > 0 && (
                <div
                  aria-hidden
                  className='px-1.5 pb-0.5 pt-1.5 text-[9px] font-semibold uppercase tracking-wider text-gray-400'
                >
                  All pages
                </div>
              )}
            </>
          )}
          {pageRows.map((row) => (
            <MenuRow
              key={row.id}
              {...rowProps(row)}
              // The page whose tab is open reads as where the user is.
              emphasised={row.kind === 'page' && onPath(row)}
              entry={row.kind === 'page' ? entries.find((e) => e.id === row.id) : undefined}
              onToggle={(expanded) => setExpanded(row.id, expanded)}
            />
          ))}
        </div>
        {rows.length === 0 && (
          <div className='flex flex-col items-center gap-1.5 px-4 py-8 text-center'>
            <SearchX className='h-6 w-6 text-gray-300' />
            <p className='text-xs text-gray-500'>
              No page or tab matches <span className='font-medium text-gray-700'>“{query.trim()}”</span>
            </p>
          </div>
        )}
      </div>

      <div className='flex shrink-0 items-center gap-2 border-t border-gray-100 bg-gray-50/80 px-2.5 py-1 text-[10px] text-gray-500'>
        {tenantName && (
          <span className='flex min-w-0 items-center gap-1.5'>
            <Building2 className='h-3 w-3 shrink-0' />
            <span className='truncate'>{tenantName}</span>
          </span>
        )}
        <span className='ml-auto flex shrink-0 items-center gap-1'>
          <KeyHint keys='↑↓' label='Move' />
          <KeyHint keys='↵' label='Open' />
          <KeyHint keys='Esc' label='Close' />
        </span>
      </div>
    </>
  )
}

function KeyHint({ keys, label }: { keys: string; label: string }) {
  return (
    <kbd title={label} className='rounded border border-gray-200 bg-white px-1 font-sans text-[9px] font-medium text-gray-500'>
      {keys}
    </kbd>
  )
}

/** The label with the filter's match in it marked. */
function Highlight({ text, query }: { text: string; query: string }) {
  const at = query ? text.toLowerCase().indexOf(query) : -1
  if (at < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, at)}
      <mark className='rounded-sm bg-amber-100 text-inherit'>{text.slice(at, at + query.length)}</mark>
      {text.slice(at + query.length)}
    </>
  )
}

type RowProps = {
  row: MainMenuRow
  query: string
  active: boolean
  current: boolean
  onHover: () => void
  onNavigate: () => void
}

/** The thin bar that marks where the user is. */
function CurrentBar() {
  return <span aria-hidden className='absolute inset-y-1 left-0 w-0.5 rounded-full bg-blue-600' />
}

function HomeRow({ row, query, active, current, onHover, onNavigate }: RowProps) {
  return (
    <div
      id={rowDomId(row.id)}
      role='treeitem'
      aria-level={1}
      aria-selected={active}
      aria-current={current ? 'page' : undefined}
      data-pinned=''
      onMouseMove={onHover}
      className={cn(
        'relative rounded-md transition-colors',
        current ? 'bg-blue-50' : active ? 'bg-gray-100' : 'bg-white',
      )}
    >
      {current && <CurrentBar />}
      <Link to={row.href} tabIndex={-1} onClick={onNavigate} className='flex h-7 items-center gap-2 px-1.5'>
        <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gradient-to-br from-blue-500 to-blue-700'>
          <Home className='h-3 w-3 text-white' />
        </span>
        <span
          data-row-label=''
          className={cn('text-xs font-semibold', current ? 'text-blue-800' : 'text-gray-800')}
        >
          <Highlight text={row.label} query={query} />
        </span>
      </Link>
    </div>
  )
}

function MenuRow({
  row,
  query,
  active,
  current,
  emphasised,
  entry,
  onHover,
  onToggle,
  onNavigate,
}: RowProps & {
  emphasised: boolean
  entry: MainMenuEntry | undefined
  onToggle: (expanded: boolean) => void
}) {
  const highlighted = current || emphasised

  if (row.kind === 'tab') {
    return (
      <div
        id={rowDomId(row.id)}
        role='treeitem'
        aria-level={2}
        aria-selected={active}
        aria-current={current ? 'page' : undefined}
        onMouseMove={onHover}
        className={cn(
          'relative ml-4 rounded-md transition-colors',
          current ? 'bg-blue-50' : active && 'bg-gray-100',
        )}
      >
        {/* Tabs hang off a guide line under their page's icon. */}
        <span aria-hidden className='absolute -left-px inset-y-0 border-l border-gray-200' />
        {current && <CurrentBar />}
        <Link
          to={row.href}
          tabIndex={-1}
          onClick={onNavigate}
          className={cn(
            'flex h-6 items-center gap-2 pl-[1.125rem] pr-2 text-[11.5px]',
            current ? 'font-semibold text-blue-700' : 'text-gray-600 hover:text-gray-900',
          )}
        >
          <span data-row-label='' className='truncate'>
            <Highlight text={row.label} query={query} />
          </span>
        </Link>
      </div>
    )
  }

  const Icon = entry ? iconForDashboardNav(entry.label) : null
  const tile = homeTile(row.id)?.tile ?? 'bg-gray-500'
  const tabCount = entry?.tabs.length ?? 0

  return (
    <div
      id={rowDomId(row.id)}
      role='treeitem'
      aria-level={1}
      aria-selected={active}
      aria-expanded={row.hasTabs ? row.expanded : undefined}
      aria-current={current ? 'page' : undefined}
      onMouseMove={onHover}
      className={cn(
        'relative flex items-center rounded-md pr-0.5 transition-colors',
        highlighted ? 'bg-blue-50' : active && 'bg-gray-100',
      )}
    >
      {highlighted && <CurrentBar />}
      <Link
        to={row.href}
        tabIndex={-1}
        onClick={onNavigate}
        className='flex h-7 min-w-0 flex-1 items-center gap-2 pl-1.5'
      >
        <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded', tile)}>
          {Icon && <Icon className='h-3 w-3 text-white' />}
        </span>
        <span
          data-row-label=''
          className={cn(
            'truncate text-xs',
            highlighted ? 'font-semibold text-blue-800' : 'font-medium text-gray-700',
          )}
        >
          <Highlight text={row.label} query={query} />
        </span>
      </Link>
      {row.hasTabs && (
        <button
          type='button'
          tabIndex={-1}
          disabled={row.forced}
          aria-label={row.expanded ? `Hide ${row.label} tabs` : `Show ${row.label} tabs`}
          onClick={() => onToggle(!row.expanded)}
          className='flex h-6 shrink-0 items-center gap-0.5 rounded px-1 text-gray-400 transition-colors hover:bg-white hover:text-gray-700 disabled:pointer-events-none disabled:opacity-60'
        >
          {!row.expanded && (
            <span aria-hidden className='rounded-full bg-gray-100 px-1 text-[9px] font-semibold leading-4 text-gray-500'>
              {tabCount}
            </span>
          )}
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-200', row.expanded && 'rotate-90')} />
        </button>
      )}
    </div>
  )
}
